import apiSlice from 'app/apiSlice';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { sortToString } from 'helpers/paramHelpers';

// Create an entity adapter for users
const audiencesAdapter = createEntityAdapter();

const audiencesApi = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getAudiences: builder.query({
      query: params => ({
        url: `audiences`,
        params: {
          ...params,
          sort: sortToString(params.sort || []),
        },
      }),
      transformResponse: response => {
        const normalized = audiencesAdapter.setAll(
          audiencesAdapter.getInitialState(),
          response.data || []
        );

        const restructured = {
          ...normalized,
          entities: normalized.ids.map(id => normalized.entities[id]), // Keep the order
        };

        return {
          ...response,
          data: restructured,
        };
      },
      providesTags: ['Audiences'], //tag for refetch when invalidateTags api trggered
    }),

    getAudienceById: builder.query({
      query: id => ({
        url: `audiences/${id}`,
        params: {
          model: 'AudienceDetail',
        },
      }),
      transformResponse: response => {
        const normalized = audiencesAdapter.setAll(
          audiencesAdapter.getInitialState(),
          response.data || []
        );

        const restructured = {
          ...normalized,
          entities: normalized.ids.map(id => normalized.entities[id]), // Keep the order
        };

        return {
          ...response,
          data: restructured,
        };
      },
      providesTags: ['Audience'], //tag for refetch when invalidateTags api trggered
      invalidatesTags: ['Audiences'], //trigger refetch for the providesTags api
    }),

    createAudience: builder.mutation({
      query: data => ({
        url: `audiences`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Audiences', 'Audience'], //trigger refetch for the providesTags api
    }),

    updateAudience: builder.mutation({
      query: ({ id, data }) => ({
        url: `audiences/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Audiences', 'Audience'], //trigger refetch for the providesTags api
    }),

    deleteAudience: builder.mutation({
      query: id => ({
        url: `audiences/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Audiences', 'Audience'], //trigger refetch for the providesTags api
    }),

    getOrderFieldSchemas: builder.query({
      query: () => ({
        url: `order_field_schemas`,
      }),
      transformResponse: response => response.data || [],
      providesTags: ['OrderFieldSchemas'],
    }),
  }),
  overrideExisting: false, // Avoid overwriting existing endpoints
});

export const {
  useGetAudiencesQuery,
  useGetAudienceByIdQuery,
  useLazyGetAudiencesQuery,
  useLazyGetAudienceByIdQuery,
  useCreateAudienceMutation,
  useUpdateAudienceMutation,
  useDeleteAudienceMutation,
  useGetOrderFieldSchemasQuery,
} = audiencesApi;

export default audiencesApi;
